import { useCallback, useEffect, useState } from "react";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AddPaymentTermFormDialog,
  EditPaymentTermFormDialog,
} from "@/features/payment-terms";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, formatNumber, statusColors, statusLabels, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const PaymentTerms = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [paymentTermId, setPaymentTermId] = useState(null);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setRowCount, paginationParams, dataGridPaginationProps, resetParams } =
    useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadPaymentTerms = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("/payment-terms", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
        },
      });
      setPaymentTerms(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat syarat pembayaran"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadPaymentTerms();
  }, [loadPaymentTerms]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_PAYMENT_TERMS) && (
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
        >
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari syarat pembayaran"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={paymentTerms}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 1.5,
              sortable: false,
            },
            {
              field: "description",
              headerName: "Deskripsi",
              flex: 2,
              sortable: false,
            },
            {
              field: "dueDays",
              headerName: "Masa Jatuh Tempo (hari)",
              flex: 1.5,
              type: "number",
              valueFormatter: (value) => formatNumber(value),
              sortable: false,
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: (params) => {
                const actions = [];
                if (hasPermission(PermissionEnum.UPDATE_PAYMENT_TERMS)) {
                  actions.push(
                    <IconButton
                      key="edit"
                      onClick={() => setPaymentTermId(params.id)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>

      {/* Add */}
      {isAddDialogOpen && (
        <AddPaymentTermFormDialog
          onClose={() => setIsAddDialogOpen(false)}
          onSubmitted={loadPaymentTerms}
        />
      )}

      {/* Edit */}
      {paymentTermId != null && (
        <EditPaymentTermFormDialog
          paymentTermId={paymentTermId}
          onClose={() => setPaymentTermId(null)}
          onSubmitted={loadPaymentTerms}
        />
      )}
    </PageContainer>
  );
};
